import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { ProductSearchFilterOptions } from '@app/akita/api/products/models/product-search.filter-options.model';
import {
  ProductSearchFilters,
  generateFiltersID,
} from '@app/akita/api/products/models/product-search.filters.model';
import { AkitaProductsQuery } from '@app/akita/api/products/state/products.query';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { PopsySkeletonComponent } from '@app/components/skeleton/popsy-skeleton.component';
import { ProductBestVariantsComponent } from '@app/products/components/product-best-variants/product-best-variants.component';
import { ProductColorVariantsComponent } from '@app/products/components/product-color-variants/product-color-variants.component';
// eslint-disable-next-line max-len
import { ProductConditionVariantsComponent } from '@app/products/components/product-condition-variants/product-condition-variants.component';
import { ProductInsuranceOptionsComponent } from '@app/products/components/product-insurance-options/product-insurance-options.component';
import { ProductKeyboardVariantsComponent } from '@app/products/components/product-keyboard-variants/product-keyboard-variants.component';
import { ProductNetworkVariantsComponent } from '@app/products/components/product-network-variants/product-network-variants.component';
import { ProductReviewsStatsComponent } from '@app/products/components/product-reviews-stats/product-reviews-stats.component';
// eslint-disable-next-line max-len
import { ProductScreenSizeVariantsComponent } from '@app/products/components/product-screen-size-variants/product-screen-size-variants.component';
import { ProductSimVariantsComponent } from '@app/products/components/product-sim-variants/product-sim-variants.component';
import { ProductStorageVariantsComponent } from '@app/products/components/product-storage-variants/product-storage-variants.component';
import { ProductStrapVariantsComponent } from '@app/products/components/product-strap-variants/product-strap-variants.component';
import { PlugIconComponent } from '@app/shared/components/icons/generated/plug/plug.component';
import { SpecUsbIconComponent } from '@app/shared/components/icons/generated/spec-usb/spec-usb.component';
import { TranslocoModule } from '@ngneat/transloco';
import { Observable, combineLatest, map, of } from 'rxjs';

@Component({
  selector: 'app-featured-details-card',
  templateUrl: './featured-details-card.template.html',
  styleUrls: ['./featured-details-card.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    TranslocoModule,
    RouterModule,
    ProductStorageVariantsComponent,
    ProductScreenSizeVariantsComponent,
    ProductColorVariantsComponent,
    ProductReviewsStatsComponent,
    ProductKeyboardVariantsComponent,
    ProductStrapVariantsComponent,
    ProductConditionVariantsComponent,
    ProductBestVariantsComponent,
    ProductNetworkVariantsComponent,
    ProductInsuranceOptionsComponent,
    ProductSimVariantsComponent,
    PlugIconComponent,
    PopsySkeletonComponent,
    SpecUsbIconComponent,
  ],
})
export class FeaturedDetailsCardComponent implements OnChanges {
  @Input() public productId?: string | null;
  @Input() public sellerId?: string | null;
  @Input() public currency?: string | null;
  @Input() public model?: string | null;
  @Input() public category?: string | null;
  @Input() public insuranceIds?: Array<string> | null;

  public readonly biggerThan$: Observable<ScreenSizeInfoState>;

  public availableVariants$: Observable<ProductSearchFilterOptions | null> = of(null);
  public isFetchingVariants$: Observable<boolean | null>;

  private filters: ProductSearchFilters;
  private filtersPath: string;

  constructor(
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaProductsQuery: AkitaProductsQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery
  ) {
    this.biggerThan$ = this.akitaScreenQuery.selectBiggerThan();

    this.filters = {
      ...new ProductSearchFilters(),
      country: this.akitaProductsQuery.country,
      language: this.akitaProductsQuery.language,
    };
    if (this.model?.length) {
      this.filters.model = [...new Array(0), this.model];
    }

    this.filtersPath = generateFiltersID(this.filters);

    this.isFetchingVariants$ = this.akitaProductsQuery.selectIsFetchingProductFilters(
      this.filtersPath
    );

    this.availableVariants$ = combineLatest([
      this.akitaProductsQuery.selectProductFilters(this.filtersPath || ''),
      this.akitaProductsQuery.selectProductVariants(this.model || ''),
    ]).pipe(
      map(([options, variants]) => {
        if (options && variants.length) {
          return options;
        }
        return null;
      })
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.model) {
      this.filters = {
        ...new ProductSearchFilters(),
        country: this.akitaProductsQuery.country,
        language: this.akitaProductsQuery.language,
      };
      if (this.model?.length) {
        this.filters.model = [...new Array(0), this.model];
      }
      this.filtersPath = generateFiltersID(this.filters);

      this.isFetchingVariants$ = this.akitaProductsQuery.selectIsFetchingProductFilters(
        this.filtersPath
      );
      this.availableVariants$ = combineLatest([
        this.akitaProductsQuery.selectProductFilters(this.filtersPath || ''),
        this.akitaProductsQuery.selectProductVariants(this.model || ''),
      ]).pipe(
        map(([options, variants]) => {
          if (options && variants.length) {
            return options;
          }
          return null;
        })
      );
      /*  this.subscriptions.add(
        this.akitaProductsService.getAvailableProductOptionsAsync(this.model || '')
      ); */
    }
  }

  public scrollToReviews($event: Event): void {
    try {
      if (this.akitaRouterQuery.isBrowser && window.document) {
        $event.stopPropagation();
        $event.preventDefault();
        document.getElementById('reviewsCard')?.scrollIntoView();
      }
    } catch {
      // SSR
    }
  }
}
